import React, { RefObject, useEffect, useState } from 'react'
import { Link, Route, Switch, useLocation, useParams } from 'react-router-dom'
import { Badge, Card, Layout, Menu } from 'antd'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import {
	FileProtectOutlined,
	ScheduleOutlined,
	CalendarOutlined,
	SettingOutlined,
	UserOutlined,
	StarOutlined,
	GiftOutlined,
	InfoCircleOutlined,
	IdcardOutlined,
} from '@ant-design/icons'

import Account from './Account'
import CallsList from './CallsList'
import Scheduler from './Scheduler'
import Admin from '../admin/Admin'
import Subscriptions from './Subscriptions'
import UpgradeUser from '../admin/users/UpdradeUser'

import PageNotFound from '../../helpers/PageNotFound'

import { stripePKey } from '../../../config'
import { useAuthContext } from '../../../context/auth'
import { AccountProvider } from '../../../context/account'
import { CallsListProvider } from '../../../context/call/CallsListProvider'
import { useChannelContext } from '../../../context/channel'
import { useExpertProfilesContext } from '../../../context/expertProfile'
import { KeywordsProvider } from '../../../context/keyword'
import { TChannel, TExpertPage, TExpertProfile } from '../../../context/types'
import SwitchAccountNotice from '../../helpers/SwitchAccountNotice'
import { RoutePath } from '../../routes'
import AdditionalInfo from './AdditionalInfo/AdditionalInfo'
import Payouts from './Payouts'
import './profile.less'
import ExpertPages from './ExpertPages'
import { useCustomFieldsContext } from '../../../context/customFields'
import { useBrandContext } from '../../../context/brand'

const stripePromise = loadStripe(stripePKey)
const { Sider, Content } = Layout

type UrlParam = {
	accountId: string
}

export default function Profile() {
	const { pathname } = useLocation()
	const { auth } = useAuthContext()
	const { expertProfiles, actions } = useExpertProfilesContext()
	const { channel } = useChannelContext()
	const { actions: customFieldsActions } = useCustomFieldsContext()
	const { brand } = useBrandContext()
	const { accountId } = useParams() as UrlParam
	const [collapsed, setCollapsed] = useState(false)
	const [openedKeys, setOpenedKeys] = useState(['profile-page-key'])
	const siderRef: RefObject<HTMLDivElement> = React.createRef()
	const collapsedWidth = 480
	const clientWidth = document.documentElement.clientWidth
	const hidePerks = channel.channels?.some(({ integration_config }: TChannel) => integration_config.hide_perks)
	const hideSubscriptions = channel.channels?.some(
		({ integration_config }: TChannel) => integration_config.hide_subscriptions
	)

	useEffect(() => {
		if (accountId) {
			customFieldsActions.clearCustomFields()

			return () => {
				customFieldsActions.clearCustomFields()
			}
		}
	}, [])

	useEffect(() => {
		if (!accountId && auth?.user?.isExpert) {
			actions.setError()
			actions.getExpertProfiles()
		}
	}, [auth?.user?.isExpert])

	const isUserProfileNotFilled = !Boolean(auth?.user?.profile_pic_url)

	const isExpertProfileNotFilled =
		expertProfiles.profiles.length &&
		expertProfiles.profiles.some(
			(profile: TExpertProfile) =>
				profile.pages?.every((page) => !page.bio) ||
				!profile.pages.length ||
				profile.pages.some((page: TExpertPage) => !page.title || !page.body || !page.keywords || !page.keywords.length)
		)

	const expertHaveToSetPayRate =
		brand.currentBrand?.experts_can_set_rates &&
		(channel?.channels || []).reduce(
			(acc, { integration_config, is_expert }) =>
				acc || (is_expert && integration_config?.experts_can_set_rates !== false),
			false
		) &&
		!expertProfiles.profiles[0]?.pay_rate

	const onMenuClick = () => {
		if (collapsedWidth >= clientWidth) {
			setCollapsed((collapsed) => !collapsed)
		}
	}

	return (
		<KeywordsProvider>
			<Layout className="profile">
				{(auth?.user?.is_admin || (!accountId && !auth?.user?.is_admin)) && (
					<Sider
						ref={siderRef}
						breakpoint="xs"
						collapsedWidth="0"
						className="slider"
						collapsed={collapsed}
						onCollapse={(collapsed: boolean) => setCollapsed(collapsed)}
					>
						<Menu
							defaultSelectedKeys={[pathname]}
							style={{ height: '100%', marginTop: 20, paddingRight: 10 }}
							mode="inline"
							onClick={onMenuClick}
							openKeys={openedKeys}
						>
							<Menu.Item
								key={accountId ? `/account/${accountId}` : '/profile'}
								icon={<UserOutlined />}
								style={{ position: 'relative' }}
							>
								<Badge dot={!accountId && auth?.user?.isExpert && isUserProfileNotFilled} />
								<Link to={accountId ? `/account/${accountId}` : '/profile'}>Account Info</Link>
							</Menu.Item>

							{(auth?.user?.isExpert || accountId) && (
								<Menu.SubMenu
									style={{ position: 'relative' }}
									title={
										<span className="menu-item">
											<Badge dot={!accountId && auth?.user?.isExpert && isExpertProfileNotFilled} />
											Profile Page
										</span>
									}
									icon={<IdcardOutlined />}
									key="profile-page-key"
									onTitleClick={() => setOpenedKeys(openedKeys.length ? [] : ['profile-page-key'])}
								>
									{expertProfiles.profiles[0]?.pages?.map(
										({ channel }) =>
											channel && (
												<Menu.Item
													key={
														accountId
															? `/account/${accountId}/expert-pages/${channel.name.toLowerCase()}`
															: `/profile/expert-pages/${channel.name.toLowerCase()}`
													}
													style={{ position: 'relative' }}
												>
													<Badge />
													<Link
														to={
															accountId
																? `/account/${accountId}/expert-pages/${channel.name.toLowerCase()}`
																: `/profile/expert-pages/${channel.name.toLowerCase()}`
														}
													>
														{channel.name}
													</Link>
												</Menu.Item>
											)
									)}
								</Menu.SubMenu>
							)}

							<Menu.Item key="/profile/additional-info" icon={<InfoCircleOutlined />} style={{ position: 'relative' }}>
								<Link to={accountId ? `/account/${accountId}/additional-info` : '/profile/additional-info'}>
									Additional Info
								</Link>
							</Menu.Item>
							<Menu.Item key="/profile/calls" icon={<ScheduleOutlined />}>
								<Link to={accountId ? `/account/${accountId}/calls` : '/profile/calls'}>My Calls</Link>
							</Menu.Item>
							{channel.showPayouts && (
								<Menu.Item key="/profile/payouts" icon={<FileProtectOutlined />}>
									<Badge dot={!accountId && auth?.user?.isExpert && expertHaveToSetPayRate} />
									<Link to={accountId ? `/account/${accountId}/payouts` : '/profile/payouts'}>Payouts</Link>
								</Menu.Item>
							)}
							{(auth.user?.isExpert || (auth.user?.is_admin && accountId)) && (
								<Menu.Item key="/profile/scheduler" icon={<CalendarOutlined />}>
									<Badge dot={!expertProfiles.profiles[0]?.user?.expertInfo?.cronofy_filled} />
									<Link to={accountId ? `/account/${accountId}/scheduler` : '/profile/scheduler'}>My Availability</Link>
								</Menu.Item>
							)}
							{!accountId && (
								<>
									{!hidePerks && (
										<Menu.Item key={RoutePath.Perks} icon={<GiftOutlined />}>
											<Link to={RoutePath.Perks}>Perks</Link>
										</Menu.Item>
									)}

									{!hideSubscriptions && (
										<Menu.Item key="/profile/subscriptions" icon={<StarOutlined />}>
											<Link to="/profile/subscriptions">Subscription Plans</Link>
										</Menu.Item>
									)}

									{auth?.user?.is_admin && (
										<Menu.Item key="/profile/admin" icon={<SettingOutlined />}>
											<Link to="/profile/admin">Admin Console</Link>
										</Menu.Item>
									)}
								</>
							)}
						</Menu>
					</Sider>
				)}
				<div
					className={`private-content ${
						collapsedWidth >= clientWidth && collapsed ? '' : 'mobile-hidden'
					} subscription-plans-container`}
				>
					<Content>
						<Card style={{ margin: 10 }}>
							<Switch>
								<Route exact path="/profile">
									<Account />
								</Route>

								<Route exact path="/profile/expert-pages/:channelName">
									<ExpertPages />
								</Route>

								<Route exact path="/profile/additional-info">
									<AdditionalInfo />
								</Route>

								<Route exact path="/profile/calls">
									<CallsListProvider>
										<CallsList />
									</CallsListProvider>
								</Route>

								<Route exact path="/profile/subscriptions">
									<Elements stripe={stripePromise}>
										<Subscriptions />
									</Elements>
								</Route>

								{channel.showPayouts && (
									<Route path="/profile/payouts">
										<Payouts />
									</Route>
								)}

								{auth?.user?.isExpert && (
									<Route path="/profile/scheduler">
										<Scheduler />
									</Route>
								)}

								{auth?.user?.is_admin && (
									<>
										<AccountProvider>
											<SwitchAccountNotice />

											<Route exact path="/account/:accountId">
												<Account />
											</Route>

											<Route exact path="/account/:accountId/expert-pages/:channelName">
												<ExpertPages />
											</Route>

											<Route exact path="/account/:accountId/additional-info">
												<AdditionalInfo />
											</Route>
											<Route exact path="/account/:accountId/calls">
												<CallsListProvider>
													<CallsList />
												</CallsListProvider>
											</Route>
											<Route path="/account/:accountId/payouts">
												<Payouts />
											</Route>
											<Route path="/account/:accountId/scheduler">
												<Scheduler />
											</Route>
										</AccountProvider>
										<Route exact path="/profile/admin">
											<Admin />
										</Route>
										<Route exact path="/profile/admin/upgrade/:id">
											<UpgradeUser />
										</Route>
									</>
								)}
								<Route>
									<PageNotFound />
								</Route>
							</Switch>
						</Card>
					</Content>
				</div>
			</Layout>
		</KeywordsProvider>
	)
}
